/*
 * Email logs: pure response shaping, pinned to email_enums.json + the fixtures via
 * adapters.test.js. The JSX renders only what these return.
 */
import { EM_DASH, api_error, is_permission_error, num } from '../format';
import { with_ist } from '../shared/ist';
import { l1_view } from '../overview/adapters';

const str = (v) => (typeof v === 'string' ? v : '');

// The teacher list reuses Overview's /l1 read (name + slug only, no amounts on this tab yet).
export const teacher_rows_view = (data) => {
  const view = l1_view(data);
  return {
    rows: view.rows.map((r) => ({
      teacher_uuid: str(r.teacher_uuid),
      teacher_name: str(r.teacher_name) || EM_DASH,
      teacher_slug: str(r.teacher_slug)
    })),
    total_count: view.total_count
  };
};

export const SENDING_NOT_ENABLED_TEXT = 'Sending is not enabled yet.';

// send_blockers are {code, message} objects (email_enums.json send_blocker_codes); the server's
// message is shown as-is (Ops-readable already), the code only labels an unrecognised one.
export const blocker_text = (b) => {
  if (b && typeof b === 'object') return str(b.message) || str(b.code);
  return str(b);
};

// Preview only (never what is sent): images and tables fit the pop-up's width and long words wrap,
// so a phone shows the whole email. Goes first in <head> when there is one, so the email's own
// rules come later; the !important keeps the fit.
export const PREVIEW_FIT_CSS =
  '<style>img,table{max-width:100%!important;width:auto!important}' +
  'body{margin:0;overflow-wrap:anywhere}</style>';
export const fit_preview_html = (html) => {
  const h = str(html);
  const head = h.match(/<head[^>]*>/i);
  if (head) return h.replace(head[0], `${head[0]}${PREVIEW_FIT_CSS}`);
  return `${PREVIEW_FIT_CSS}${h}`;
};

export const DRAFT_TEXT = 'Draft: this month is not closed, so the figures may still change.';

// One info banner: draft note (unless MONTH_NOT_CLOSED says it), blockers, "not enabled"; no repeats.
export const preview_notice_lines = (d, is_draft, can_send) => {
  const raw = Array.isArray(d.send_blockers) ? d.send_blockers : [];
  const says_draft = raw.some((b) => b && typeof b === 'object' && b.code === 'MONTH_NOT_CLOSED');
  return [
    is_draft && !says_draft ? DRAFT_TEXT : '',
    ...raw.map(blocker_text),
    can_send ? '' : SENDING_NOT_ENABLED_TEXT
  ].filter(Boolean);
};

export const preview_view = (data) => {
  const d = data || {};
  const is_draft = str(d.month_lifecycle) !== 'CLOSED';
  const can_send = d.can_send === true;
  return {
    notice_lines: preview_notice_lines(d, is_draft, can_send),
    subject: str(d.subject),
    html: str(d.html),
    to_display: str(d.recipient_display),
    is_test: str(d.delivery_mode || 'TEST') === 'TEST',
    is_draft,
    can_send,
    blockers: (Array.isArray(d.send_blockers) ? d.send_blockers : []).map(blocker_text),
    truncated: d.truncated === true,
    statement_hash: str(d.statement_hash),
    payable_amount: num(d.payable_amount) // what the statement pays, to the paisa
  };
};

const text_row = (t) => {
  const r = t || {};
  return {
    id: num(r.id),
    pay_month: r.pay_month === null || r.pay_month === undefined ? null : str(r.pay_month),
    is_default: r.is_default === true,
    greeting: str(r.greeting),
    intro: str(r.intro),
    footer: str(r.footer),
    created_by_name: str(r.created_by_name) || str(r.created_by),
    created_at: with_ist(str(r.created_at_ist))
  };
};

export const text_view = (data) => {
  const d = data || {};
  return {
    ...text_row(d.effective),
    placeholders: Array.isArray(d.placeholders) ? d.placeholders.map(str) : [],
    history: (Array.isArray(d.history) ? d.history : []).map(text_row)
  };
};

// POST /email/text: {text, replayed}. A replay (the same client_request_id, same body) is not an
// error — the row was already saved by an earlier attempt of this same save.
export const save_text_view = (data) => {
  const d = data || {};
  return { text: text_row(d.text), replayed: d.replayed === true };
};

// error_keys (email_enums.json): status, reason, message, error_code, fields. `fields` is an array of
// {code, field, message} on INVALID_TEXT — turned into {field: message} for the form to key off.
export const field_errors_view = (e) => {
  const fields = (e && e.response && e.response.data && e.response.data.fields) || [];
  const out = {};
  (Array.isArray(fields) ? fields : []).forEach((f) => {
    if (f && f.field) out[f.field] = str(f.message);
  });
  return out;
};

// Same error-routing convention as close_versions/adapters.cv_error_view: FORBIDDEN -> the shell's
// no-permission page; everything else renders inline with the server's sentence.
export const email_error_view = (e, fallback) => {
  const { code, message } = api_error(e);
  if (is_permission_error(e) || code === 'FORBIDDEN') return { kind: 'permission', code };
  return { kind: 'inline', code, text: message || fallback };
};

// the Payment emails tab owns delivery; this tab only previews, so it points there.
export const EMAIL_LOGS_NOTE =
  'Preview only: nothing is sent from this tab. Payment emails follow the switch on the Payment emails tab in production; outside production they only ever go to the test inboxes listed there.';
